import React from "react";
import {
  AutoTextarea,
  SectionCard,
  getFieldStyle,
  getLabelStyle,
  getDisplayValueWithNone,
  inputStyle,
} from "./patientChartShared";
import { formatDateTimeForDisplay } from "../../utils/patient/patientUtils";

export default function PatientInfoSection({
  patientForm = {},
  setPatientField,
  expandedSections,
  toggleSection,
  sectionRef,
  activeSection,
  readOnlyMode = false,
  errorCount = 0,
}) {
  function handleAllergiesNoneChange(checked) {
    if (readOnlyMode) return;
    setPatientField("allergiesNone", checked);
    setPatientField("allergies", checked ? "None" : "");
  }

  function handleMedicationsNoneChange(checked) {
    if (readOnlyMode) return;
    setPatientField("medicationsNone", checked);
    setPatientField("medications", checked ? "None" : "");
  }

  function handlePastMedicalHistoryNoneChange(checked) {
    if (readOnlyMode) return;
    setPatientField("pastMedicalHistoryNone", checked);
    setPatientField("pastMedicalHistory", checked ? "None" : "");
  }

  const noneLabelStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    fontSize: 13,
    fontWeight: 700,
    color: "#334155",
  };

  const fieldHeaderStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: 10,
    marginBottom: 4,
  };

  return (
    <SectionCard
      title="Patient Information"
      sectionKey="patientInfo"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={sectionRef}
      activeSection={activeSection}
      errorCount={errorCount}
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
          <label style={getLabelStyle(patientForm.fullName, true)}>
            Full Name
            <input
              style={getFieldStyle(patientForm.fullName, true)}
              value={patientForm.fullName || ""}
              onChange={(e) => setPatientField("fullName", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>

          <label style={getLabelStyle(patientForm.dob, true)}>
            DOB
            <input
              type="date"
              style={getFieldStyle(patientForm.dob, true)}
              value={patientForm.dob || ""}
              onChange={(e) => setPatientField("dob", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>

          <label style={getLabelStyle(patientForm.age, true)}>
            Age
            <input
              style={getFieldStyle(patientForm.age, true)}
              value={patientForm.age || ""}
              onChange={(e) => setPatientField("age", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>

          <label style={getLabelStyle(patientForm.gender, true)}>
            Gender
            <select
              style={getFieldStyle(patientForm.gender, true)}
              value={patientForm.gender || ""}
              onChange={(e) => setPatientField("gender", e.target.value)}
              disabled={readOnlyMode}
            >
              <option value="">Select</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
            </select>
          </label>

          <label style={getLabelStyle(patientForm.phone, true)}>
            Phone
            <input
              style={getFieldStyle(patientForm.phone, true)}
              value={patientForm.phone || ""}
              onChange={(e) => setPatientField("phone", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>

          <label style={getLabelStyle(patientForm.email, true)}>
            Email
            <input
              style={getFieldStyle(patientForm.email, true)}
              value={patientForm.email || ""}
              onChange={(e) => setPatientField("email", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              window.innerWidth <= 768
                ? "1fr"
                : "minmax(220px, 2fr) repeat(3, minmax(120px, 1fr))",
            gap: 10,
          }}
        >
          <label style={getLabelStyle(patientForm.streetAddress, true)}>
            Street Address
            <input
              style={getFieldStyle(patientForm.streetAddress, true)}
              value={patientForm.streetAddress || ""}
              onChange={(e) => setPatientField("streetAddress", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>

          <label style={getLabelStyle(patientForm.city, true)}>
            City
            <input
              style={getFieldStyle(patientForm.city, true)}
              value={patientForm.city || ""}
              onChange={(e) => setPatientField("city", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>

          <label style={getLabelStyle(patientForm.state, true)}>
            State
            <input
              style={getFieldStyle(patientForm.state, true)}
              value={patientForm.state || ""}
              onChange={(e) => setPatientField("state", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>

          <label style={getLabelStyle(patientForm.zip, true)}>
            Zip
            <input
              style={getFieldStyle(patientForm.zip, true)}
              value={patientForm.zip || ""}
              onChange={(e) => setPatientField("zip", e.target.value)}
              disabled={readOnlyMode}
            />
          </label>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12 }}>
          <div>
            <div style={fieldHeaderStyle}>
              <div
                style={getLabelStyle(
                  getDisplayValueWithNone(
                    patientForm.allergies,
                    patientForm.allergiesNone
                  ),
                  true
                )}
              >
                Allergies
              </div>

              <label style={noneLabelStyle}>
                <input
                  type="checkbox"
                  checked={Boolean(patientForm.allergiesNone)}
                  onChange={(e) => handleAllergiesNoneChange(e.target.checked)}
                  disabled={readOnlyMode}
                />
                None
              </label>
            </div>

            <AutoTextarea
              value={
                patientForm.allergiesNone
                  ? "None"
                  : patientForm.allergies || ""
              }
              onChange={(e) => setPatientField("allergies", e.target.value)}
              style={{
                ...getFieldStyle(
                  patientForm.allergiesNone ? "None" : patientForm.allergies,
                  true
                ),
                minHeight: 72,
              }}
              disabled={readOnlyMode || Boolean(patientForm.allergiesNone)}
            />
          </div>

          <div>
            <div style={fieldHeaderStyle}>
              <div
                style={getLabelStyle(
                  getDisplayValueWithNone(
                    patientForm.medications,
                    patientForm.medicationsNone
                  ),
                  true
                )}
              >
                Medications
              </div>

              <label style={noneLabelStyle}>
                <input
                  type="checkbox"
                  checked={Boolean(patientForm.medicationsNone)}
                  onChange={(e) => handleMedicationsNoneChange(e.target.checked)}
                  disabled={readOnlyMode}
                />
                None
              </label>
            </div>

            <AutoTextarea
              value={
                patientForm.medicationsNone
                  ? "None"
                  : patientForm.medications || ""
              }
              onChange={(e) => setPatientField("medications", e.target.value)}
              style={{
                ...getFieldStyle(
                  patientForm.medicationsNone
                    ? "None"
                    : patientForm.medications,
                  true
                ),
                minHeight: 72,
              }}
              disabled={readOnlyMode || Boolean(patientForm.medicationsNone)}
            />
          </div>
        </div>

        <div>
          <div style={fieldHeaderStyle}>
            <div
              style={getLabelStyle(
                getDisplayValueWithNone(
                  patientForm.pastMedicalHistory,
                  patientForm.pastMedicalHistoryNone
                ),
                true
              )}
            >
              Past Medical History
            </div>

            <label style={noneLabelStyle}>
              <input
                type="checkbox"
                checked={Boolean(patientForm.pastMedicalHistoryNone)}
                onChange={(e) =>
                  handlePastMedicalHistoryNoneChange(e.target.checked)
                }
                disabled={readOnlyMode}
              />
              None
            </label>
          </div>

          <AutoTextarea
            value={
              patientForm.pastMedicalHistoryNone
                ? "None"
                : patientForm.pastMedicalHistory || ""
            }
            onChange={(e) =>
              setPatientField("pastMedicalHistory", e.target.value)
            }
            style={{
              ...getFieldStyle(
                patientForm.pastMedicalHistoryNone
                  ? "None"
                  : patientForm.pastMedicalHistory,
                true
              ),
              minHeight: 72,
            }}
            disabled={
              readOnlyMode || Boolean(patientForm.pastMedicalHistoryNone)
            }
          />
        </div>

        <div style={{ display: "grid", gap: 4 }}>
          <div style={{ fontSize: 11, color: "#64748b" }}>Last Seen</div>
          <div
            style={{
              ...inputStyle,
              minHeight: 38,
              display: "flex",
              alignItems: "center",
              background: "#f8fafc",
            }}
          >
            {formatDateTimeForDisplay(patientForm.lastEncounterAt) || "—"}
          </div>
        </div>
      </div>
    </SectionCard>
  );
}